import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge as IdIcon, CameraAlt, ChevronLeft, PhoneIphone } from "@mui/icons-material";
import { Box, Paper, Skeleton, Typography, useTheme } from "@mui/material";
import { Button, PageHeader } from "@components/ui_v2";
import { ALTO_SKELETON_EXPEDIENTE, IDENTITY_VALIDATION_NUMBER_OF_ATTEMPTS } from "@helpers/constantes";
import { useLazyGetPersonByIdQuery } from "@api/personasApi";
import { useLazyGetMetaMapDataQuery } from "@api/metaMapApi";
import { MetaDataJson } from "@interfaces/catalogs";
import { ApiError } from "@interfaces/response";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import * as Sentry from "@sentry/react";
import { useSelector } from "react-redux";

const ITEMS = [
  {
    icon: <IdIcon fontSize="small" />,
    label: "Identificación oficial vigente",
    sub: "INE, pasaporte o cédula profesional",
  },
  {
    icon: <CameraAlt fontSize="small" />,
    label: "Selfie con prueba de vida",
    sub: "Toma una foto con buena iluminación",
  },
  {
    icon: <PhoneIphone fontSize="small" />,
    label: "Cámara con permisos activados",
    sub: "Necesitamos acceder a la cámara para la captura",
  },
];

export const ValidacionIdentidadPersona = () => {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const metamapButtonRef = useRef<HTMLDivElement>(null);

  const application = useSelector((state: RootState) => state.register.application);

  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getDataMetaMap] = useLazyGetMetaMapDataQuery();

  const [isLoading, setIsLoading] = useState(true);
  const [enableButton, setEnableButton] = useState(false);
  const [metaDataJson, setMetaDataJson] = useState<MetaDataJson | null>(null);

  const goBack = useCallback(() => navigate(-1), [navigate]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);

      if (!personId) {
        goBack();
        return;
      }

      const person = await getPersonById(personId).unwrap();

      if (!person || person.loan_application_id !== application.id || person.identity_validation_completed) {
        goBack();
        return;
      }

      const metamap_res = await getDataMetaMap(person.id).unwrap();
      const metamap_info = metamap_res?.data;

      if (metamap_info.attempts > IDENTITY_VALIDATION_NUMBER_OF_ATTEMPTS) {
        goBack();
        return;
      }

      setMetaDataJson({
        client_details_id: metamap_info.client_details_id,
        loan_application_id: metamap_info.loan_application_id,
        person_id: metamap_info.person_id,
        person_type: metamap_info.person_type,
        regimen: metamap_info.regimen,
        attempts: metamap_info.attempts,
        upload_type: metamap_info.upload_type,
      });

      setEnableButton(true);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ValidacionIdentidadPersona-loadData" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  }, [personId, goBack, application.id, getPersonById, getDataMetaMap, dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleUserFinishedSdk = () => {
      setMetaDataJson(null);
      setEnableButton(false);
      goBack();
    };

    const metamapButton = metamapButtonRef.current;
    if (metamapButton) {
      metamapButton.addEventListener("metamap:userFinishedSdk", handleUserFinishedSdk as EventListener);
    }
    return () => {
      if (metamapButton) {
        metamapButton.removeEventListener("metamap:userFinishedSdk", handleUserFinishedSdk as EventListener);
      }
    };
  }, [goBack]);

  return (
    <Box>
      <PageHeader
        eyebrow="Expediente · Validación de identidad"
        title="Validemos tu identidad"
        subtitle="Realizaremos un proceso biométrico. Tendrás que tomarte una selfie y fotografiar tu identificación oficial."
      />

      {isLoading ? (
        <Skeleton variant="rectangular" sx={{ height: ALTO_SKELETON_EXPEDIENTE, borderRadius: 2 }} />
      ) : (
        <>
          <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {ITEMS.map((it) => (
                <Box key={it.label} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: "primary.base",
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {it.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      {it.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {it.sub}
                    </Typography>
                  </Box>
                </Box>
              ))}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 1,
                  pointerEvents: !enableButton ? "none" : "auto",
                  opacity: !enableButton ? 0.5 : 1,
                }}
              >
                <metamap-button
                  ref={metamapButtonRef}
                  clientid={import.meta.env.VITE_META_CLIENT_ID}
                  flowId={import.meta.env.VITE_META_FLOW_ID}
                  merchantToken={import.meta.env.VITE_MERCHANT_TOKEN}
                  language="es"
                  metadata={JSON.stringify(metaDataJson || {})}
                  color={theme?.palette?.primary?.main}
                />
              </Box>
            </Box>
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, gap: 2 }}>
            <Button tone="ghost" startIcon={<ChevronLeft fontSize="small" />} onClick={goBack}>
              Regresar
            </Button>
            <Box />
          </Box>
        </>
      )}
    </Box>
  );
};
